import { Component } from '@theme/component';
import {
  onDocumentLoaded,
  changeMetaThemeColor,
  setHeaderMenuStyle,
  lockScroll,
  unlockScroll,
  onAnimationEnd,
  prefersReducedMotion,
} from '@theme/utilities';
import {
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
  scrollTo,
} from '@theme/scroll-container';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  /**
   * Owner correction (2026-08-11): stock Horizon requires 'headerMenu' -
   * ref="headerMenu" is only rendered by blocks/_header-menu.liquid's
   * 'fixed_menus' variant (the desktop inline nav bar, gated on
   * block_settings.information_menu having visible links), which is
   * architecturally unreachable now that the header is unified to always
   * use the drawer treatment at every breakpoint (assets/utilities.js,
   * setHeaderMenuStyle()). this.refs.headerMenu was never actually read
   * anywhere in this file - confirmed root cause of a live
   * MissingRefError: Component#updateRefs() (assets/component.js) threw
   * synchronously on every connectedCallback, which aborted the rest of
   * this method before it reached the theme-drawer:close/
   * header-drawer:close listener registrations below - so #onOverlayClosed
   * never ran, #activeOverlay/scroll-lock were set on open but never
   * cleared on close, and the shared scroll-lock owners Set (assets/
   * utilities.js) stayed permanently locked. Dropped the dead requirement
   * rather than restoring the obsolete element or the ref name.
   */
  requiredRefs = ['headerDrawerContainer', 'headerRowTop'];

  /**
   * Owner request (2026-08-09, "TEST" full-screen overlay experiment):
   * Menu and Cart are mutually-exclusive sibling UI states
   * (activeOverlay = null | menu | cart), not independently-openable
   * overlays. This is the single source of truth for which one (if
   * either) is currently active, and the trigger to restore focus to
   * once the WHOLE overlay session ends - set once on the first open of
   * a session and preserved across any number of Menu <-> Cart switches,
   * never reset to the switch trigger itself.
   * @type {'menu' | 'cart' | null}
   */
  #activeOverlay = null;

  /** @type {HTMLElement | null} */
  #overlayOriginTrigger = null;

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /** @type {EventTarget | null} */
  #scrollContainer = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
      root: getIntersectionRoot(),
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
      // The drawer squeeze can trigger minimum-reached at desktop widths where
      // it normally wouldn't. Once the menu hides, the overflow-list is
      // display:none and can't measure to clear it. Resetting it here so
      // setHeaderMenuStyle() sees a clean state.
      const overflowList = this.querySelector('overflow-list');
      if (overflowList) overflowList.removeAttribute('minimum-reached');
    }
    setHeaderMenuStyle();
  }

  /**
   * Owner request (2026-08-23): the logo's own href (blocks/_header-
   * logo.liquid, routes.root_url) already navigates to Home on click -
   * this only covers the case where we're already there, where a plain
   * link click can leave scroll position untouched (same-URL navigations
   * aren't guaranteed to reset scroll the way a genuinely new page load
   * does). Off Home, this is a no-op and the native navigation proceeds
   * unchanged - a real page load unmounts the drawer along with
   * everything else, so there's nothing to close explicitly there.
   *
   * Owner request (2026-08-24): the logo is still reachable (and
   * visible, via z-index) while the Menu drawer is open on top of the
   * page - clicking it from inside Menu on Home must also close the
   * drawer, not just scroll to top. Reuses header-drawer's own guarded
   * close() (same call switchTo() above already makes for the
   * equivalent "close this same overlay" case) so the closing animation
   * and its existing scroll-lock/overlay-state teardown
   * (HeaderDrawerCloseEvent -> #onOverlayClosed below) run exactly as
   * they already do for every other close path - nothing new to wire up
   * here. Guarded on the drawer's own open state first: close() isn't
   * built to no-op cleanly when nothing is open (it still waits on
   * onAnimationEnd for a transition that will never start), and this
   * fires on every Home-logo click, not just ones made from inside an
   * open Menu.
   *
   * Owner request (2026-09-07): "Logo navigation while cart is open" - same
   * reasoning applies to the Cart drawer (#cart-drawer, snippets/cart-
   * drawer.liquid), which this method never touched before - a Home-logo
   * click while Cart was open scrolled to top but silently left the cart
   * drawer open on screen. Reuses #cart-drawer's own guarded close(), the
   * exact same call switchTo() above already makes for its "close this same
   * overlay" case (line ~264), gated the same way on #activeOverlay so this
   * never fires outside an actual open-cart state.
   *
   * @param {Event} event
   */
  handleLogoClick(event) {
    if (!(event.target instanceof HTMLAnchorElement)) return;
    if (new URL(event.target.href).pathname !== window.location.pathname) return;

    event.preventDefault();
    scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'instant' : 'smooth' });

    const menu = document.querySelector('header-drawer');
    if (menu?.querySelector('details')?.open) {
      /** @type {any} */ (menu).close();
    }

    if (this.#activeOverlay === 'cart') {
      const cart = document.querySelector('#cart-drawer');
      /** @type {any} */ (cart)?.close();
    }
  }

  /**
   * Routes the Menu and Cart trigger clicks (header-drawer.liquid,
   * header-actions.liquid) through one mutually-exclusive overlay state
   * instead of each trigger calling its own component's toggle()
   * independently, which would let both end up open at once (stacking) or
   * leave switching between them exposing the storefront as an
   * intermediate step.
   *
   * Three cases:
   * - Nothing active: open the target fresh, remembering this trigger as
   *   the one to restore focus to once the whole overlay session ends.
   * - The target is already the active overlay: this is its own trigger,
   *   now showing "close" - close it normally (skipFocusRestore left
   *   false, so it restores focus itself; #onOverlaySystemClosed below
   *   only clears state and the scroll lock this method added).
   * - A DIFFERENT overlay is active: switch. Close the current one with
   *   skipFocusRestore so it doesn't yank focus away a moment later, open
   *   the target, and keep the ORIGINAL session's origin trigger
   *   (never reset to the switch trigger) so the eventual close still
   *   returns focus to where the whole session actually started.
   *
   * @param {{ target: 'menu' | 'cart' }} data
   * @param {Event} event
   */
  switchTo(data, event) {
    const { target: targetName } = data;
    if (targetName !== 'menu' && targetName !== 'cart') return;
    if (!(event.target instanceof HTMLElement)) return;

    // Queried fresh each time rather than cached: Section Rendering API
    // morphs can replace either node between opens (see theme-drawer.js's
    // own equivalent comment on its focus-restore fallback).
    const menu = document.querySelector('header-drawer');
    const cart = document.querySelector('#cart-drawer');
    if (!menu || !cart) return;

    if (this.#activeOverlay === targetName) {
      if (targetName === 'menu') {
        /** @type {any} */ (menu).close();
      } else {
        /** @type {any} */ (cart).close();
      }
      return;
    }

    const isSwitch = this.#activeOverlay !== null;
    const origin = isSwitch ? this.#overlayOriginTrigger : event.target;

    if (isSwitch) {
      // Owner request (2026-08-21): a direct Menu<->Cart switch never
      // passes through a fully closed/no-overlay frame. Root cause:
      // the outgoing drawer's close animation and the incoming
      // drawer's open animation both run independently (neither
      // await's the other), each fading/sliding relative to its OWN
      // fully-transparent/off-screen "closed" state - on desktop
      // (both breakpoints fade opacity) two simultaneous 0<->1
      // opacity ramps can never sum to full coverage at any
      // in-between instant, briefly revealing the storefront under
      // both; on mobile (both slide via transform) the two motions
      // are only gap-free if perfectly frame-synced, which isn't
      // guaranteed. data-overlay-switching keeps ONE shared, already-
      // opaque backdrop (sections/header.liquid) active underneath
      // both drawers for exactly this window - it does not touch,
      // fade, or replace either drawer's own existing animation
      // (still its own breakpoint-specific duration/easing/direction,
      // untouched below and in each drawer's own CSS), it just
      // guarantees there's nothing but drawer-colored surface behind
      // them while they cross over. Cleared once the INCOMING
      // drawer's own real animation finishes (onAnimationEnd, the
      // same Web Animations API helper both drawers already use
      // internally) - not a fixed timer, so it tracks whatever each
      // breakpoint's actual duration is without hardcoding it here.
      document.documentElement.setAttribute('data-overlay-switching', '');

      if (this.#activeOverlay === 'menu') {
        /** @type {any} */ (menu).close({ skipFocusRestore: true });
      } else {
        /** @type {any} */ (cart).close({ skipFocusRestore: true });
      }
    } else {
      // First overlay of a new session - the scroll lock now spans the
      // whole session (including any later switches), not just this one
      // overlay's own open/close. Each component's own lockScroll/
      // unlockScroll calls stay in effect too (safe no-ops/redundant adds
      // against the same reference-counted Set, assets/utilities.js) -
      // this is only needed because the two components' own scroll-lock
      // timing differs enough (Cart unlocks immediately on close; Menu's
      // native <details> `toggle` event, which assets/dialog.js listens
      // to for its own lock/unlock, fires as a queued task, not
      // synchronously) that relying on them alone could theoretically let
      // the shared owners Set hit zero for a moment mid-switch.
      lockScroll(this);
    }

    this.#activeOverlay = targetName;
    this.#overlayOriginTrigger = origin;

    document.documentElement.dataset.activeOverlay = targetName;

    if (targetName === 'menu') {
      /** @type {any} */ (menu).open(undefined, event, { restoreFocusTo: origin });
    } else {
      /** @type {any} */ (cart).open({ restoreFocusTo: origin });
    }

    if (isSwitch) {
      // The element each drawer's own CSS actually transitions (see the
      // data-overlay-switching comment above) - Menu animates
      // .menu-drawer itself, Cart animates .theme-drawer__dialog.
      const incomingAnimatedElement =
        targetName === 'menu' ? menu.querySelector('.menu-drawer') : cart.querySelector('.theme-drawer__dialog');

      if (incomingAnimatedElement) {
        // Menu's own open() (header-drawer.js) defers its transition-
        // triggering class add to a requestAnimationFrame - calling
        // onAnimationEnd() synchronously here (same tick as menu.open()
        // above) would query getAnimations() before that class exists,
        // find nothing running, and resolve immediately, clearing the
        // flag before the crossfade even starts. Cart's open() (theme-
        // drawer.js) sets its class synchronously, so it doesn't need
        // this, but deferring one frame is harmless there too. Queuing
        // our own requestAnimationFrame after menu.open()/cart.open()
        // means it runs after Menu's internal one (same frame, FIFO
        // registration order) - the exact same "add class, then
        // onAnimationEnd" sequencing header-drawer.js's own open()/
        // #close() already rely on, just split across two callers.
        requestAnimationFrame(() => {
          onAnimationEnd(
            incomingAnimatedElement,
            () => {
              document.documentElement.removeAttribute('data-overlay-switching');
            },
            { subtree: false }
          );
        });
      } else {
        document.documentElement.removeAttribute('data-overlay-switching');
      }
    }
  }

  /**
   * Keeps #activeOverlay in sync when a drawer closes through a path other
   * than switchTo() itself - Escape, most notably, which each component
   * still handles entirely on its own (assets/theme-drawer.js,
   * assets/header-drawer.js). Guarded on #activeOverlay still matching the
   * closing drawer so a stale/switched-away-from close (Cart's own close
   * event fires synchronously when switchTo() calls it with
   * skipFocusRestore during a switch, before #activeOverlay is reassigned
   * a couple of lines later) can't incorrectly clear state that a
   * same-tick switchTo() call is in the middle of setting.
   *
   * @param {Event} event
   */
  #onOverlayClosed = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const isCart = target.id === 'cart-drawer' && this.#activeOverlay === 'cart';
    const isMenu = target.tagName.toLowerCase() === 'header-drawer' && this.#activeOverlay === 'menu';
    if (!isCart && !isMenu) return;

    this.#activeOverlay = null;
    this.#overlayOriginTrigger = null;
    delete document.documentElement.dataset.activeOverlay;
    unlockScroll(this);
  };

  /**
   * Rebinds the scroll listener and IntersectionObserver when the viewport
   * crosses the squeeze breakpoint (990px). The scroll container switches
   * between `.page-wrapper` (desktop) and `document.scrollingElement` (mobile),
   * so cached bindings from initialization become stale after a resize.
   */
  #handleBreakpointChange = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) return;

    // Rebind scroll listener
    if (this.#scrollContainer) {
      this.#scrollContainer.removeEventListener('scroll', this.#handleWindowScroll);
      this.#scrollContainer = getScrollEventTarget();
      this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
    }

    // Recreate IntersectionObserver with the new root
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#observeStickyPosition(stickyMode === 'always');
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = getScrollTop();
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    // Document-level, not `this` - #cart-drawer (snippets/cart-drawer.liquid)
    // is rendered in layout/theme.liquid, outside header-component's own
    // subtree, so its bubbled events never reach a listener scoped to this
    // element.
    document.addEventListener('theme-drawer:close', this.#onOverlayClosed);
    document.addEventListener('header-drawer:close', this.#onOverlayClosed);

    // setHeaderMenuStyle() (assets/utilities.js) otherwise only ever runs
    // reactively — from the overflowMinimum listener above, or from
    // assets/theme-editor.js (Theme Editor only, not a normal storefront
    // visit). If that reactive chain doesn't fire on a given load (depends
    // on overflow-list's own async IntersectionObserver-driven measurement
    // completing), data-menu-style is never set at all, and the header
    // falls back to unstyled default CSS until something else (e.g. a
    // resize) happens to re-trigger it. Called directly here so the
    // correct value is set unconditionally on connect, independent of
    // that event chain.
    setHeaderMenuStyle();

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        this.#scrollContainer = getScrollEventTarget();
        this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
      }

      scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('theme-drawer:close', this.#onOverlayClosed);
    document.removeEventListener('header-drawer:close', this.#onOverlayClosed);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    this.#scrollContainer?.removeEventListener('scroll', this.#handleWindowScroll);
    this.#scrollContainer = null;
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
